import useOptimisticMoveProjectToPipelineStage from '@/api/mutations/project/optimistic/useOptimisticMoveProjectToPipelineStage';
import useGetMyRegionPreferences from '@/api/queries/me/useGetMyRegionPreferences';
import {
  GetPipelineStageProjectResponse,
  useGetPipelineStageInfiniteProjects,
} from '@/api/queries/pipelines/pipelineStageProjects';
import useGetPipelineStage from '@/api/queries/pipelines/useGetPipelineStage';
import useGetProjectPotentialPipelineTransitions from '@/api/queries/project/useGetProjectPotentialPipelineTransitions';
import { GetPipelineStageProjectsInput } from '@/gql/graphql';
import {
  getDisallowedTransitionsSet,
  getTransitionGuardResult,
} from '@/helpers/pipelinesHelper';
import usePreferredCurrencyFormat from '@/hooks/usePreferredCurrencyFormat';
import { useTranslation } from '@/i18n';
import { ProjectCardLinkWithData } from '@/ui/pages/pipelinePage/components/ProjectCardWithData';
import { useDndContext } from '@dnd-kit/core';
import { useToastContext } from '@payaca/ambrosia';
import PipelineStage from '@payaca/components/pipelineStage/PipelineStage';
import { Draggable, Droppable, type DragEndEventWithOver } from '@payaca/dnd';

import ProjectCard from '@payaca/components/projectCard/ProjectCard';
import { useLocalStorage, usePersistedScrollPosition } from '@payaca/hooks';
import { useObserveIntersection } from '@payaca/hooks/useObserveIntersection';
import { clstx } from '@payaca/utilities/cvaMerge';
import { FC } from 'react';

import useIsContainerBeyondBreakpoint from '@/hooks/useIsContainerBeyondBreakpoint';
import {
  getPipelineStageScrollPositionKey,
  PIPELINE_SCROLL_POSITION_MAX_AGE_MS,
} from '@/ui/pages/pipelinePage/pipelineScrollPosition';

const PROJECT_CARD_DRAG_TYPE = 'PROJECT_CARD';

export type DraggableProjectCardData = {
  type: typeof PROJECT_CARD_DRAG_TYPE;
  projectPublicId: string;
  pipelinePublicId: string;
  pipelineStagePublicId: string;
  project: GetPipelineStageProjectResponse;
};

export const isDraggableProjectCardData = (
  data: any
): data is DraggableProjectCardData => {
  return Boolean(data && data.type === PROJECT_CARD_DRAG_TYPE);
};

// `Memory` being the open / closed state of pipeline stages in local storage
const PipelineStageWithMemory: FC<{
  pipelinePublicId: string;
  pipelineStagePublicId: string;
  input?: GetPipelineStageProjectsInput;
}> = (props) => {
  const { pipelinePublicId, pipelineStagePublicId, input } = props;

  /**
   * i18n
   */
  const t = useTranslation();

  /**
   * state
   */
  const [isOpenLocalStorage, setIsOpen] = useLocalStorage(
    'pipeline-stage-open-' + pipelineStagePublicId,
    true
  );
  const isMdAndUp = useIsContainerBeyondBreakpoint('md');
  // default to open if no local storage value (even tho we set an initial value above... react-use types quirk)
  // On mobile, ignore the persisted preference: stages can't be collapsed
  // (the toggle button is hidden) and rely on horizontal swiping instead.
  const isOpen = isMdAndUp ? (isOpenLocalStorage ?? true) : true;

  /**
   * context
   */
  const dndContext = useDndContext();
  const { pushError } = useToastContext();

  /**
   * queries
   */
  const { pipelineStage } = useGetPipelineStage({
    pipelinePublicId,
    pipelineStagePublicId,
  });
  const {
    pipelineStageProjects,
    hasNextPage,
    isFetching,
    fetchNextPage,
    totalProjectValue,
    totalCount,
  } = useGetPipelineStageInfiniteProjects({
    pipelinePublicId,
    pipelineStagePublicId,
    input,
  });

  /**
   * side effect queries
   */
  const { potentialPipelineTransitions } =
    useGetProjectPotentialPipelineTransitions(
      dndContext.active?.id as string | undefined
    );

  const disallowedTransitionsSet = potentialPipelineTransitions
    ? getDisallowedTransitionsSet(potentialPipelineTransitions)
    : undefined;

  /**
   * mutations
   */
  const { mutateAsync: moveProjectToPipelineStageMutation } =
    useOptimisticMoveProjectToPipelineStage(pipelinePublicId, input);

  /**
   * side effects
   */
  const skeletonLoaderRef = useObserveIntersection(
    () => hasNextPage && !isFetching && void fetchNextPage()
  );

  /**
   * derived dnd context state
   */
  const isAnyDragging = Boolean(dndContext.active);
  const isOverNothing = isAnyDragging && !dndContext.over;
  const isOverOrigin =
    isAnyDragging &&
    dndContext.over?.id ===
      dndContext.active?.data.current?.['pipelineStagePublicId'];
  const isOverSelf = dndContext.over?.id === pipelineStagePublicId;
  const isOverDisallowedColumn = Boolean(
    dndContext.over &&
    disallowedTransitionsSet?.has(dndContext.over.id as string)
  );
  const isOrigin =
    dndContext.active?.data.current?.['pipelineStagePublicId'] ===
    pipelineStagePublicId;
  const shouldHighlightColumn =
    isOverSelf || isOverOrigin || isOverNothing || isOverDisallowedColumn;
  const shouldErrorColumn =
    isAnyDragging &&
    Boolean(disallowedTransitionsSet?.has(pipelineStagePublicId));
  const guardResultGroups =
    shouldErrorColumn && potentialPipelineTransitions
      ? getTransitionGuardResult(
          t,
          potentialPipelineTransitions,
          pipelineStagePublicId
        )
      : undefined;
  const hideChildren = isAnyDragging && !isOrigin;

  const scrollRef = usePersistedScrollPosition({
    key: getPipelineStageScrollPositionKey(
      pipelinePublicId,
      pipelineStagePublicId,
      input
    ),
    maxAgeMs: PIPELINE_SCROLL_POSITION_MAX_AGE_MS,
    enabled:
      !!pipelineStageProjects && !!pipelineStage && isOpen && !hideChildren,
  });

  /**
   * callbacks
   */
  const handleDrop = (event: DragEndEventWithOver) => {
    const activeData = event.active.data.current;

    if (!isDraggableProjectCardData(activeData)) {
      console.warn(
        'PipelineStageWithMemory: Dropped item is not a draggable project card.'
      );
      return;
    }

    if (shouldErrorColumn) {
      pushError("You can't move the project to the selected stage.");
      return;
    }

    moveProjectToPipelineStageMutation({
      projectPublicId: activeData.projectPublicId,
      targetPipelineStagePublicId: pipelineStagePublicId,
      currentPipelineStagePublicId: activeData.pipelineStagePublicId,
    })
      .then(() => {
        // success
      })
      .catch((err) => {
        // error
        pushError(
          "Couldn't move project to the selected stage. Please try again."
        );
      });
  };

  const { data: myRegionPreferences } = useGetMyRegionPreferences();

  const totalProjectValueString = usePreferredCurrencyFormat(
    totalProjectValue || {
      value: 0,
      currency: myRegionPreferences?.currency || { code: 'GBP', exponent: 2 },
    }
  );

  // show skeleton only when we have no data at all --> placeholder while loading
  if (!pipelineStageProjects || !pipelineStage) {
    return (
      // oxlint-disable-next-line payaca/no-tailwind-arbitrary-values -- Viewport-relative size
      <PipelineStage.SkeletonLoader className="w-[calc(100vw-2rem)] md:w-80 md:min-w-80 shrink-0" />
    );
  }

  return (
    <>
      {/*todo: reopen closed stage if item is dragged over it for x seconds*/}
      <Droppable
        id={pipelineStagePublicId}
        disabled={!isOpen}
        onDrop={handleDrop}
      >
        <PipelineStage
          className={clstx(
            // oxlint-disable-next-line payaca/no-tailwind-arbitrary-values -- Viewport-relative size
            isOpen && 'w-[calc(100vw-2rem)] md:w-80',
            'shrink-0',
            'snap-center md:snap-align-none'
          )}
          isOpen={isOpen}
          onToggle={setIsOpen}
          stageName={pipelineStage.title}
          stageColour={pipelineStage.colour}
          stageValue={totalProjectValueString}
          projectCount={totalCount}
          highlighted={isOpen && shouldHighlightColumn}
          errorHighlighted={isOpen && shouldErrorColumn}
          guardResultGroups={guardResultGroups}
          hideChildren={hideChildren}
          scrollRef={scrollRef}
        >
          {pipelineStageProjects?.map((project) => (
            <Draggable<DraggableProjectCardData>
              key={project.publicId}
              id={project.publicId}
              data={{
                type: PROJECT_CARD_DRAG_TYPE,
                projectPublicId: project.publicId,
                pipelinePublicId,
                pipelineStagePublicId,
                project,
              }}
            >
              <ProjectCardLinkWithData
                className="data-dragging:opacity-50"
                project={project}
              />
            </Draggable>
          ))}

          {hasNextPage && (
            <ProjectCard.SkeletonLoader ref={skeletonLoaderRef} />
          )}
        </PipelineStage>
      </Droppable>
    </>
  );
};

export default PipelineStageWithMemory;
